import dayjs from 'dayjs';
import {useEffect, useMemo} from 'react';
import type {UseFormReturn} from 'react-hook-form';
import {useTranslation} from 'react-i18next';

import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormRequiredLegend,
} from '@/components/ui/form';
import {Input} from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {FormSkeleton} from '@/components/ui/skeleton';
import {graphqlClient} from '@/services/graphqlClient';
import {useListHealthLogMetricsQuery} from '@/services/graphqlHooks';
import ResidentInput from '@/shared/residents/components/ResidentInput';
import healthLogMetrics from '@/translation/healthLogMetrics';
import healthLogMetricsFields from '@/translation/healthLogMetricsFields';
import {TIME_FORMATS} from '@/utils/timeFormats';

import type {HealthLogMeasurementFormValues} from '../utils/healthFormInput';

export type HealthLogMeasurementFormFieldsProps = {
  form: UseFormReturn<HealthLogMeasurementFormValues>;
  isEdit?: boolean;
};

const HealthLogMeasurementFormFields = ({
  form,
  isEdit,
}: HealthLogMeasurementFormFieldsProps) => {
  const {t} = useTranslation();

  const {data: metricsData, isLoading} =
    useListHealthLogMetricsQuery(graphqlClient);
  const metrics = metricsData?.listHealthLogMetrics ?? [];

  const metricId = form.watch('metricId');
  const selectedMetric = useMemo(
    () => metrics.find(metric => metric.id === metricId),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [metricId]
  );

  useEffect(() => {
    if (isEdit) return;

    const values = selectedMetric?.fields?.reduce(
      (acc, field) => ({
        ...acc,
        [field.name]: null,
      }),
      {}
    );

    form.setValue('values', values || {});
  }, [metricId, form, selectedMetric, isEdit]);

  if (isLoading) {
    return <FormSkeleton size={5} />;
  }

  return (
    <>
      <FormRequiredLegend />

      <FormField
        control={form.control}
        name="recordedAt"
        render={({field}) => (
          <FormItem>
            <FormLabel required>{t('Date and time')}</FormLabel>
            <FormControl>
              <Input
                {...field}
                type="datetime-local"
                required
                value={dayjs(field.value || undefined).format(
                  TIME_FORMATS.dateTimeHTMLLocal
                )}
                onChange={evt => {
                  field.onChange(
                    dayjs(evt.target.value || undefined).toISOString()
                  );
                }}
                disabled={isEdit}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="residentId"
        render={({field}) => (
          <FormItem>
            <FormLabel required>{t('Resident')}</FormLabel>
            <FormControl>
              <ResidentInput
                inputProps={{
                  disabled: isEdit,
                }}
                value={field.value}
                onChange={field.onChange}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="metricId"
        render={({field}) => (
          <FormItem>
            <FormLabel required>{t('Metric')}</FormLabel>
            <FormControl>
              <Select
                disabled={isEdit}
                onValueChange={field.onChange}
                value={field.value || ''}
              >
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder={t('Select metric')} />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {metrics.map(metric => (
                    <SelectItem key={metric.id} value={metric.id}>
                      {healthLogMetrics[metric.id] || metric.id}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      {selectedMetric?.fields?.map(fieldDef => (
        <FormField
          key={fieldDef.name}
          control={form.control}
          name={`values.${fieldDef.name}` as const}
          render={({field}) => (
            <FormItem>
              <FormLabel required>
                {healthLogMetricsFields[fieldDef.name] || fieldDef.name}
                {fieldDef.unit ? ` (${fieldDef.unit})` : ''}
              </FormLabel>
              <FormControl>
                <Input
                  {...field}
                  type="number"
                  step="any"
                  inputMode="decimal"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      ))}

      {selectedMetric && (
        <FormField
          control={form.control}
          name="values"
          render={({fieldState}) => (
            <FormItem>{fieldState.error?.message && <FormMessage />}</FormItem>
          )}
        />
      )}
    </>
  );
};

export default HealthLogMeasurementFormFields;
